import React, { useEffect, useState } from 'react';
import useDeviceTelemetry from '../../DeviceTelemetry/hooks/useDeviceTelemetry';
import useActivationDevices from './hooks/useActivationDevices';
import Loading from '../../Common/components/Loading';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import moment from 'moment-timezone';
import { Tablet, Pencil, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/src/shadComponents/ui/input';

interface QueueData {
    deviceKeyId: string;
    deviceId: string;
    queueData: Record<string, unknown>;
    updatedAt: string;
    name?: string;
}

interface ActivationQueueDataProps {
    activationId: number;
}

interface EditingState {
    deviceKeyId: string;
    value: string;
}

// Merge queue data with activation device data
interface MergedDeviceData {
    deviceKeyId: string;
    deviceId?: string;
    deviceName: string | null;
    friendlyName: string | null;
    displayName: string;
    lastUploadAt: string | null;
    queueData?: {
        uploaded?: number;
        unuploaded?: number;
    };
    updatedAt?: string;
}

export const ActivationQueueData: React.FC<ActivationQueueDataProps> = ({
    activationId,
}) => {
    const [queueData, setQueueData] = useState<QueueData[]>([]);
    const { getQueueDataForActivation, isLoading: queueLoading, error: queueError } =
        useDeviceTelemetry();
    const { 
        devices: activationDevices, 
        isLoading: devicesLoading, 
        error: devicesError,
        isUpdating,
        updateFriendlyName 
    } = useActivationDevices(activationId);

    const { orgId } = useUserOrg();
    const [editing, setEditing] = useState<EditingState | null>(null);

    useEffect(() => {
        const fetchQueueData = async () => {
            const data = await getQueueDataForActivation(orgId, activationId);
            if (data) {
                setQueueData(data);
            }
        };

        fetchQueueData();

        let timer = setInterval(() => {
            fetchQueueData();
        }, 10000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId, orgId]);

    const handleStartEdit = (deviceKeyId: string, currentName: string) => {
        setEditing({ deviceKeyId, value: currentName });
    };

    const handleCancelEdit = () => {
        setEditing(null);
    };

    const handleSaveEdit = async () => {
        if (!editing) return;
        
        await updateFriendlyName(editing.deviceKeyId, editing.value);
        setEditing(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSaveEdit();
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };

    const isLoading = queueLoading || devicesLoading;
    const error = queueError || devicesError;

    if (error) {
        return <div className="text-red-500">Failed to load device data</div>;
    }

    if (isLoading && !queueData.length && !activationDevices.length) {
        return <Loading isLoading={true} />;
    }

    // Merge queue data with activation devices
    // Use activation devices as the primary source, enriched with queue data
    const mergedData: MergedDeviceData[] = [];
    const seenDeviceKeys = new Set<string>();

    // First, add all activation devices (devices that have uploaded)
    for (const device of activationDevices) {
        seenDeviceKeys.add(device.deviceKeyId);
        const queueItem = queueData.find(q => q.deviceKeyId === device.deviceKeyId);
        mergedData.push({
            deviceKeyId: device.deviceKeyId,
            deviceId: queueItem?.deviceId,
            deviceName: device.deviceName,
            friendlyName: device.friendlyName,
            displayName: device.displayName,
            lastUploadAt: device.lastUploadAt,
            queueData: queueItem?.queueData as { uploaded?: number; unuploaded?: number } | undefined,
            updatedAt: queueItem?.updatedAt,
        });
    }

    // Then add any queue data entries that aren't in activation devices yet
    for (const queueItem of queueData) {
        if (!seenDeviceKeys.has(queueItem.deviceKeyId)) {
            mergedData.push({
                deviceKeyId: queueItem.deviceKeyId,
                deviceId: queueItem.deviceId,
                deviceName: queueItem.name || null,
                friendlyName: null,
                displayName: queueItem.name || 'Unknown Device',
                lastUploadAt: null,
                queueData: queueItem.queueData as { uploaded?: number; unuploaded?: number } | undefined,
                updatedAt: queueItem.updatedAt,
            });
        }
    }

    if (!mergedData.length) {
        return <div className="text-gray-500 py-4">No devices have uploaded to this activation yet.</div>;
    }

    return (
        <div className="relative w-full overflow-auto">
            <table className="w-full caption-bottom text-sm">
                <thead className="[&_tr]:border-b">
                    <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                            Photo Source
                        </th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                            Device Name
                        </th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                            Uploaded
                        </th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                            Remaining
                        </th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                            Last Upload
                        </th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="[&_tr:last-child]:border-0">
                    {mergedData.map((item, index) => (
                        <tr
                            key={item.deviceKeyId + index}
                            className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                        >
                            <td className="p-4 align-middle font-medium">
                                {editing?.deviceKeyId === item.deviceKeyId ? (
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={editing.value}
                                            onChange={(e) =>
                                                setEditing({
                                                    ...editing,
                                                    value: e.target.value,
                                                })
                                            }
                                            onKeyDown={handleKeyDown}
                                            className="h-8 w-40"
                                            autoFocus
                                            placeholder="Enter name..."
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={isUpdating === item.deviceKeyId}
                                            className="p-1 hover:bg-green-100 dark:hover:bg-green-900 rounded text-green-600"
                                            title="Save"
                                        >
                                            <Check className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-600"
                                            title="Cancel"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={
                                                item.friendlyName
                                                    ? 'font-medium'
                                                    : 'text-gray-400 italic'
                                            }
                                        >
                                            {item.displayName}
                                        </span>
                                        <button
                                            onClick={() =>
                                                handleStartEdit(
                                                    item.deviceKeyId,
                                                    item.friendlyName || '',
                                                )
                                            }
                                            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-400 hover:text-gray-600"
                                            title="Edit name"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}
                            </td>
                            <td className="p-4 align-middle">
                                <span className="text-gray-500 text-sm font-mono">
                                    {item.deviceName || 'Unknown'}
                                </span>
                            </td>
                            <td className="p-4 align-middle font-medium">
                                {item.queueData?.uploaded ?? '-'}
                            </td>
                            <td className="p-4 align-middle">
                                {item.queueData?.unuploaded ?? '-'}
                            </td>
                            <td className="p-4 align-middle">
                                {item.lastUploadAt ? (
                                    <span title={moment(item.lastUploadAt).format('MMMM D, YYYY h:mm A')}>
                                        {moment(item.lastUploadAt).fromNow()}
                                    </span>
                                ) : item.updatedAt ? (
                                    <span title={moment(item.updatedAt).format('MMMM D, YYYY h:mm A')}>
                                        {moment(item.updatedAt).fromNow()}
                                    </span>
                                ) : (
                                    <span className="text-gray-400">-</span>
                                )}
                            </td>
                            <td className="p-4 align-middle">
                                <Link
                                    to={`/orgs/${orgId}/device-telemetry/${item.deviceKeyId}`}
                                >
                                    <div className="flex items-center justify-center bg-black text-white rounded-md border border-border py-2 px-3 text-sm">
                                        <Tablet className="h-4 w-4" />
                                        <span className="ml-2">
                                            View Device
                                        </span>
                                    </div>
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
